import React, { useContext } from "react";

import { Header } from "../components/header/index.tsx";
import { Seo } from "../components/seo/index.tsx";
import { Container } from "../components/styled.ts";
import { useMobile } from "../hooks/isMobile.tsx";
import { Typography } from "../components/typography/index.tsx";
import SubscriptionTile from "../components/subscriptionTile/index.tsx";
import { subscriberContext } from "../context/subscriberContext.tsx";
import {
  Plan,
  arrangePlansForDisplay,
  isPurchasablePlan,
  maxAnnualDiscount,
} from "../utils/pricing.ts";

const GRADIENT = "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)";

const REASSURANCES = [
  "🔒 Secure payments by Razorpay",
  "💬 Alerts on WhatsApp, no app needed",
  "🚫 No spam, ever",
];

const SubscriptionPlan = () => {
  const { isMobile } = useMobile();
  const { plans, subscriberDetails } = useContext(subscriberContext);

  // Trial and Life Time Free are onboarding states, not something to sell here.
  const purchasablePlans: Plan[] = (plans || []).filter(isPurchasablePlan);
  const { ordered: sellablePlans, recommendedIndex } =
    arrangePlansForDisplay(purchasablePlans);
  const bestAnnualDiscount = maxAnnualDiscount(sellablePlans);

  return (
    <>
      <Seo
        title="Subscription Plans | FinQ India"
        description="Compare FinQ India's WhatsApp stock alert plans for NSE & BSE. Free lifetime plan for 5 stocks, paid plans for unlimited alerts on more stocks."
        path="/subscription-plans"
      />
      <Header active="subscription-plans" />

      {/* Hero */}
      <Container
        column
        width="min(calc(100% - 40px), 900px)"
        justifyContent="center"
        alignItems="center"
        margin={isMobile ? "24px auto 0" : "40px auto 0"}
        gap="14px"
      >
        <Typography
          tag="h1"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "32px" : "50px"}
          lineHeight={isMobile ? "40px" : "60px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          textAlign="center"
          margin="0"
          fontWeight={500}
        >
          Subscription{" "}
          <Typography
            tag="span"
            background={GRADIENT}
            WebkitBackgroundClip="text"
            WebkitTextFillColor="transparent"
          >
            Plans
          </Typography>
        </Typography>
      </Container>

      
      {/* Plans */}
      <Container
        width={
          isMobile
            ? "calc(100% - 40px)"
            : "min(calc(100% - 160px), 1600px)"
        }
        margin={isMobile ? "24px auto 0" : "36px auto 0"}
        justifyContent="center"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(220px, 1fr))",
          gap: isMobile ? "20px" : "30px",
          alignItems: "stretch",
        }}
      >
        {sellablePlans.length === 0
          ? [0, 1, 2].map((key) => (
              <Container
                key={key}
                minHeight="440px"
                radius="24px"
                background="#F6F7F9"
                border="solid 1px #EEE"
              />
            ))
          : sellablePlans.map((plan, index) => (
              <SubscriptionTile
                key={plan.planName}
                plan={plan}
                isRecommended={index === recommendedIndex}
              />
            ))}
      </Container>

      {/* Reassurance */}
      <Container
        width="min(calc(100% - 40px), 1100px)"
        margin={isMobile ? "28px auto 0" : "36px auto 0"}
        justifyContent="center"
        alignItems="center"
        gap={isMobile ? "12px" : "28px"}
        column={isMobile}
        wrap
      >
        {REASSURANCES.map((item) => (
          <Typography
            key={item}
            tag="span"
            fontFamily="Neue Montreal"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="0.02em"
            color="#828282"
            textAlign="center"
          >
            {item}
          </Typography>
        ))}
      </Container>

      {/* Footnotes */}
      <Container
        column
        alignItems="center"
        gap="6px"
        width="min(calc(100% - 40px), 640px)"
        margin="16px auto 48px"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize="12px"
          fontWeight={400}
          lineHeight="16px"
          color="#9CA3AF"
          textAlign="center"
          margin="0"
        >
          Every plan is a 12-month subscription, charged once.
        </Typography>
        {!subscriberDetails && (
          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize="12px"
            fontWeight={400}
            lineHeight="16px"
            color="#9CA3AF"
            textAlign="center"
            margin="0"
          >
            * Pricing varies by country. Sign up to see your personalized rates.
          </Typography>
        )}
      </Container>
    </>
  );
};

export default SubscriptionPlan;
